/**
 * Configuration for {@link RequestRateGate}. All values are in milliseconds.
 */
export interface RateGateConfig {
    /** Minimum spacing between requests after a confirmed success. */
    minIntervalMs: number;
    /** Backoff applied after the first failure; doubles per consecutive failure. */
    initialBackoffMs: number;
    /** Upper bound for the failure backoff. */
    maxBackoffMs: number;
}

/**
 * Exponential backoff for a consecutive-failure count, capped at `maxMs`.
 * A count of 1 or less yields `initialMs`; every further failure doubles it.
 */
export function getFailureBackoffMs(
    consecutiveFailures: number,
    initialMs: number,
    maxMs: number,
): number {
    const exponent = Math.max(consecutiveFailures - 1, 0);
    return Math.min(initialMs * 2 ** exponent, maxMs);
}

/**
 * Single-tab rate limiter for a polled endpoint. A plain stateful object (not
 * React state) so it can be module-scoped: remounts and render churn can never
 * reset it, which is what stops one tab storming the endpoint.
 *
 * Per request: check {@link isOpen}, {@link closePessimistically} before sending
 * (so a transport error that never resolves still holds the gate shut), then
 * exactly one of {@link recordSuccess} / {@link recordFailure}. Methods take an
 * explicit `now` to stay unit-testable without fake timers.
 */
export class RequestRateGate {
    /** Epoch ms before which no new request may be issued. */
    private nextAllowedAt = 0;
    /** Count of consecutive failures, driving the exponential backoff. */
    private consecutiveFailures = 0;

    constructor(private readonly config: RateGateConfig) {}

    /** Milliseconds until the gate reopens; `<= 0` means it is open now. */
    public msUntilOpen(now: number = Date.now()): number {
        return this.nextAllowedAt - now;
    }

    /** Whether a request may be issued at `now`. */
    public isOpen(now: number = Date.now()): boolean {
        return this.msUntilOpen(now) <= 0;
    }

    /**
     * Hold the gate shut on the assumption the in-flight request will fail,
     * using the backoff for the *next* failure. A confirmed outcome
     * ({@link recordSuccess} / {@link recordFailure}) overrides this.
     */
    public closePessimistically(now: number = Date.now()): void {
        this.nextAllowedAt =
            now + this.backoffFor(this.consecutiveFailures + 1);
    }

    /** Confirmed success: clear the failure streak and resume normal cadence. */
    public recordSuccess(now: number = Date.now()): void {
        this.consecutiveFailures = 0;
        this.nextAllowedAt = now + this.config.minIntervalMs;
    }

    /** Confirmed failure: escalate the backoff for the next attempt. */
    public recordFailure(now: number = Date.now()): void {
        this.consecutiveFailures += 1;
        this.nextAllowedAt = now + this.backoffFor(this.consecutiveFailures);
    }

    /** Clear all state, e.g. on logout. */
    public reset(): void {
        this.nextAllowedAt = 0;
        this.consecutiveFailures = 0;
    }

    private backoffFor(consecutiveFailures: number): number {
        return getFailureBackoffMs(
            consecutiveFailures,
            this.config.initialBackoffMs,
            this.config.maxBackoffMs,
        );
    }
}
